import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import styles from "./AddRegionModal.module.css";
import SelectBox from "../../BaseComponents/SelectBox/SelectBox";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import MarketingTextSection from "../MarketingText/MarketingText";
import { useToast } from "../../../context/ToastContext";
import {
  useGetHolidayTypes,
  getRegionHolidayTypes,
} from "../../../api/geo/useHolidayTypes";
import GeoImageUpload from "../GeoImageUpload/GeoImageUpload";

const emptyForm = {
  countryId: "",
  name: "",
  code: "",
  active: true,
  mainImage: "",
  holidayTypeIds: [],
  marketing: {
    title: "",
    para1: "",
    para2: ""
  }
};

const AddRegionModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  countries = [],
  onSave,
  saveLoading = false
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitError, setSubmitError] = useState(null);

  const { data: allHolidayTypes = [] } = useGetHolidayTypes({
    page: 1,
    limit: 500,
    languageCode: "ENG",
  });
  const holidayTypeOptions = Array.isArray(allHolidayTypes) ? allHolidayTypes : [];

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);

    if (mode === "edit" && initialData) {
      setForm({
        countryId: initialData.countryId ?? "",
        name: initialData.name || "",
        code: initialData.code || "",
        active: initialData.active ?? true,
        mainImage: initialData.mainImage || "",
        holidayTypeIds: [],
        marketing: {
          title: initialData.marketing?.title || "",
          para1: initialData.marketing?.para1 || "",
          para2: initialData.marketing?.para2 || ""
        }
      });
      // Preload assigned holiday types for this region
      if (initialData.id) {
        getRegionHolidayTypes(initialData.id, { languageCode: "ENG" })
          .then((res) => {
            if (res?.success && Array.isArray(res.data)) {
              setForm((prev) => ({ ...prev, holidayTypeIds: res.data.map((t) => t.id) }));
            }
          })
          .catch(() => {});
      }
    }

    if (mode === "add") {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleMarketingChange = marketing => {
    setForm(prev => ({
      ...prev,
      marketing
    }));
  };

  const toggleHolidayType = (typeId) => {
    setForm((prev) => {
      const current = prev.holidayTypeIds || [];
      const next = current.includes(typeId)
        ? current.filter((id) => id !== typeId)
        : [...current, typeId];
      return { ...prev, holidayTypeIds: next };
    });
  };

  const handleSubmit = async () => {
    if (!form.countryId || !form.name?.trim()) {
      showToast("Country and Region Name are required", "error");
      return;
    }
    if (!form.code?.trim()) {
      showToast("Region Code is required", "error");
      return;
    }
    setSubmitError(null);
    const payload = {
      countryId: Number(form.countryId),
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      active: form.active,
      mainImage: form.mainImage || null,
      holidayTypeIds: form.holidayTypeIds || [],
      title: (form.marketing?.title || "").trim(),
      paragraph1: (form.marketing?.para1 || "").trim(),
      paragraph2: (form.marketing?.para2 || "").trim(),
      description: (form.marketing?.para1 || "").trim()
    };
    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setSubmitError(err.message || "Failed to save region");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Region" : "Add New Region"}
      width="720px"
      footer={
        <>
          {submitError && (
            <span className={styles.submitError}>{submitError}</span>
          )}
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "+ Create Region"
            )}
          </button>
        </>
      }
    >
      <div className={styles.grid}>
        <div className={styles.field}>
          <label>Country</label>
          <SelectBox
            placeholder="Select country"
            value={String(form.countryId)}
            options={countries.map(c => ({
              label: c.name,
              value: String(c.id)
            }))}
            onChange={e =>
              handleChange("countryId", e.target.value)
            }
          />
        </div>

        <div className={`${styles.field} ${styles.statusField}`}>
          <label>Status</label>
          <ToggleSwitch
            checked={form.active}
            onChange={() =>
              handleChange("active", !form.active)
            }
          />
        </div>

        <div className={styles.field}>
          <label>Region Name</label>
          <input
            placeholder="e.g. Catalonia"
            value={form.name}
            onChange={e =>
              handleChange("name", e.target.value)
            }
          />
        </div>

        <div className={styles.field}>
          <label>Region Code</label>
          <input
            placeholder="e.g. CT"
            value={form.code}
            onChange={e =>
              handleChange("code", e.target.value.toUpperCase())
            }
          />
        </div>
      </div>

      <div className={styles.field}>
        <label>Main Image</label>
        <GeoImageUpload value={form.mainImage} onChange={(url) => handleChange("mainImage", url)} />
      </div>

      <div className={styles.holidayTypesSection}>
        <div className={styles.holidayTypesHeader}>
          <span className={styles.holidayTypesLabel}>Holiday types applied to this region</span>
          {(form.holidayTypeIds || []).length > 0 && (
            <span className={styles.selectedBadge}>{(form.holidayTypeIds || []).length} selected</span>
          )}
        </div>
        {holidayTypeOptions.length === 0 ? (
          <div className={styles.noTypesHint}>No holiday types available. Add types first.</div>
        ) : (
          <div className={styles.holidayTypesGrid}>
            {holidayTypeOptions.map((type) => (
              <label
                key={type.id}
                className={`${styles.holidayTypeItem} ${(form.holidayTypeIds || []).includes(type.id) ? styles.holidayTypeItemSelected : ""}`}
              >
                <input
                  type="checkbox"
                  checked={(form.holidayTypeIds || []).includes(type.id)}
                  onChange={() => toggleHolidayType(type.id)}
                />
                <span className={styles.holidayTypeIcon}>{type.icon || "•"}</span>
                <span>{type.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <MarketingTextSection
        value={form.marketing}
        onChange={handleMarketingChange}
      />

      <div className={styles.warning}>
        ⚠️ Region settings affect service availability, pricing rules, and operational reports.
      </div>
    </BaseModal>
  );
};

export default AddRegionModal;
